import { Data, Model, Utils } from "@singularsystems/neo-core";
import { AxiosPromise } from "axios";
import { injectable } from "inversify";
import { AppService, Types } from "../../App/Services/AppService";
import CommandResult from "../../Common/Models/CommandResult";
import UpdateCheckQueryConfigCommand from "../CheckQueries/Models/Commands/UpdateCheckQueryConfigCommand";
import CheckQuerySummaryLookup from "../CheckQueries/Models/Lookups/CheckQuerySummaryLookup";
import CheckQuerySummarySearchCriteria from "../CheckQueries/Models/Queries/CheckQuerySummarySearchCriteria";
import CheckQueryDropDownLookup from "../CheckQueries/Models/Lookups/CheckQueryDropDownLookup";
import ImportProcessStepDropDownLookup from "../CheckQueries/Models/Lookups/ImportProcessStepDropDownLookup";
import CreateCheckQueryConfigCommand from "../CheckQueries/Models/Commands/CreateCheckQueryConfigCommand";
import CheckQueryLookup from "../CheckQueries/Models/Lookups/CheckQueryLookup";
import TierDropdownLookup from "../CheckQueries/Models/Lookups/TierDropdownLookup";
import StoredProcedureDropdownLookup from "../CheckQueries/Models/Lookups/StoredProcedureDropdownLookup";
import CreateCheckQueryCommand from "../CheckQueries/Models/Commands/CreateCheckQueryCommand";
import UpdateCheckQueryCommand from "../CheckQueries/Models/Commands/UpdateCheckQueryCommand";


export interface ICheckQueryApiClient {
    getPagedCheckQuerySummary(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<CheckQuerySummarySearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<CheckQuerySummaryLookup>>>;
    updateCheckQueryConfig(command: Model.PlainObject<UpdateCheckQueryConfigCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    getCheckQueryDropdownList(criteria: string): AxiosPromise<Array<Model.PlainObject<CheckQueryDropDownLookup>>>;
    getPagedCheckQuery(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<CheckQuerySummarySearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<CheckQueryLookup>>>;
    getImportProcessStepDropdownList(): AxiosPromise<Array<Model.PlainObject<ImportProcessStepDropDownLookup>>>;
    createCheckQueryConfig(command: Model.PlainObject<CreateCheckQueryConfigCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    getStoredProcedureDropdownList(criteria: string, schema: string): AxiosPromise<Array<Model.PlainObject<StoredProcedureDropdownLookup>>>;
    updateCheckQuery(command: Model.PlainObject<UpdateCheckQueryCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    createCheckQuery(command: Model.PlainObject<CreateCheckQueryCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    isCheckQueryCodeUnique(criteria: string, checkQueryId: number):  AxiosPromise<Model.PlainObject<boolean>>;
}

@injectable()
export default class CheckQueryApiClient extends Data.ApiClientBase implements ICheckQueryApiClient {

    constructor(config = AppService.get(Types.Config)) {
        super(`${config.apiPath}/CheckQuery`)
    }

    public getPagedCheckQuerySummary(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<CheckQuerySummarySearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<CheckQuerySummaryLookup>>> {
        return this.axios.get(`${this.apiPath}/GetCheckQuerySummary?${Utils.getQueryString(payload)}`);
    }

    public getCheckQueryDropdownList(criteria: string):  AxiosPromise<Array<Model.PlainObject<CheckQueryDropDownLookup>>> {
        return this.axios.get(`${this.apiPath}/GetCheckQueryListByCriteria`, { params: { searchCriteria: criteria }});
    }

    public getImportProcessStepDropdownList(): AxiosPromise<Array<Model.PlainObject<ImportProcessStepDropDownLookup>>> {
        return this.axios.get(`${this.apiPath}/GetImportProcessStepDropdownList`);
    }

    public getPagedCheckQuery(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<CheckQuerySummarySearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<CheckQueryLookup>>> {
        return this.axios.get(`${this.apiPath}/GetCheckQueryPagedListQuery?${Utils.getQueryString(payload)}`);
    }

    public updateCheckQueryConfig(command: Model.PlainObject<UpdateCheckQueryConfigCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.patch(`${this.apiPath}/UpdateServiceProviderHouseCheckQuery`, command);
    }

    public createCheckQueryConfig(command: Model.PlainObject<CreateCheckQueryConfigCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.patch(`${this.apiPath}/CreateServiceProviderHouseCheckQuery`, command);
    }

    public createCheckQuery(command: Model.PlainObject<CreateCheckQueryCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.patch(`${this.apiPath}/CreateCheckQuery`, command);
    }

    public updateCheckQuery(command: Model.PlainObject<UpdateCheckQueryCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.patch(`${this.apiPath}/UpdateCheckQuery`, command);
    }

    public getTierDropdownList(): AxiosPromise<Array<Model.PlainObject<TierDropdownLookup>>>{
        return this.axios.get(`${this.apiPath}/GetTierDropdownList`);
    }

    public getStoredProcedureDropdownList(criteria: string, schema: string): AxiosPromise<Array<Model.PlainObject<StoredProcedureDropdownLookup>>>{
        return this.axios.get(`${this.apiPath}/GetStoredProcedureDropdownList?searchCriteria=` + criteria + `&schema=` + schema);
    }

    public isCheckQueryCodeUnique(criteria: string, checkQueryId: number):  AxiosPromise<Model.PlainObject<boolean>>{
        return this.axios.get(`${this.apiPath}/isCheckQueryCodeUnique?newCode=` + criteria + '&id=' + checkQueryId);
    }
}